import { sourceRevision } from './translationAssurance.js';

const ENGLISH_ONLY_JOURNEY_IDS = new Set(['waiting-for-surgery', 'easy-read-blood-test', 'easy-read-scan']);
const plain = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

/**
 * Source register of potentially patient-facing wording added by an individual
 * Trust. These records MUST NOT be included in the national language sign-off:
 * Trust publication and source revisions are separately governed.
 *
 * A configured field may still equal the national default; compare the local
 * source with its published base before marking it as genuinely customised.
 */
export function collectTrustTranslationSources(configuration = {}, trustId = configuration?.trustId) {
  const trust = String(trustId || '').trim();
  if (!trust) throw new Error('Trust ID is required for a scoped source audit');
  const rows = [];
  const add = (path, sourceText, type = 'review_required', feature = 'trust_config') => {
    if (!hasText(sourceText)) return;
    const id = `trustLocal:${trust}:${path}`;
    rows.push({
      id, trustId: trust, path, sourceText: sourceText.trim(),
      sourceRevision: sourceRevision(id, sourceText.trim()),
      sourceType: type, feature,
      reviewScope: type === 'english_only' ? 'intentionally_english_only' :
        type === 'proper_name' ? 'identity_context' : 'trust_specific_not_nationally_certified',
    });
  };
  for (const [journeyId, journeyValue] of Object.entries(plain(configuration.journeys))) {
    const journey = plain(journeyValue);
    if (journey.included === false) continue;
    const feature = `journey:${journeyId}`;
    const type = ENGLISH_ONLY_JOURNEY_IDS.has(journeyId) || journeyId.startsWith('easy-read-')
      ? 'english_only' : 'review_required';
    add(`journeys.${journeyId}.title`, journey.title, type, feature);
    add(`journeys.${journeyId}.serviceName`, journey.serviceName, 'proper_name', feature);
    add(`journeys.${journeyId}.branding.trustName`, journey.branding?.trustName, 'proper_name', feature);
    add(`journeys.${journeyId}.branding.logoAlt`, journey.branding?.logoAlt, type, feature);
    for (const [stageId, stageValue] of Object.entries(plain(journey.stageOverrides))) {
      const stage = plain(stageValue);
      add(`journeys.${journeyId}.stageOverrides.${stageId}.title`, stage.title, type, feature);
      add(`journeys.${journeyId}.stageOverrides.${stageId}.description`, stage.description, type, feature);
    }
    for (const [linkId, linkValue] of Object.entries(plain(journey.linkOverrides))) {
      const link = plain(linkValue);
      if (link.visible === false) continue;
      add(`journeys.${journeyId}.linkOverrides.${linkId}.title`, link.title, type, feature);
      add(`journeys.${journeyId}.linkOverrides.${linkId}.description`, link.description, type, feature);
    }
  }
  for (const [uniformId, uniformValue] of Object.entries(plain(configuration.uniforms))) {
    add(`uniforms.${uniformId}.name`, uniformValue?.name, 'review_required', 'custom_uniform');
  }
  for (const [roleId, roleValue] of Object.entries(plain(configuration.roles))) {
    if (roleValue?.included === false) continue;
    // Uniform notes may appear beside the patient-facing published icon.
    add(`roles.${roleId}.uniform.note`, roleValue?.uniform?.note, 'review_required', 'uniform_note');
  }
  const seen = new Set();
  for (const row of rows) {
    if (seen.has(row.id)) throw new Error('Duplicate Trust-specific source: ' + row.id);
    seen.add(row.id);
  }
  return rows;
}

export function trustTranslationCoverage(configuration, trustId) {
  const sources = collectTrustTranslationSources(configuration, trustId);
  const inScope = sources.filter((row) => row.sourceType === 'review_required');
  const intentionallyEnglishOnly = sources.filter((row) => row.sourceType === 'english_only');
  const names = sources.filter((row) => row.sourceType === 'proper_name');
  return {
    trustId: String(trustId || configuration?.trustId || ''),
    sourceCount: sources.length, needsLocalReview: inScope.length,
    deliberatelyEnglishOnly: intentionallyEnglishOnly.length, identityContext: names.length,
    status: inScope.length ? 'trust_translation_review_not_implemented' : 'no_local_reviewable_text_detected',
    disclaimer: 'A national Studio language review does not certify Trust-authored wording. This audit does not grant any local clinical or linguistic approval.',
    sources,
  };
}
